import React from "react";
import {Tooltip, Typography} from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import FlexBox from "view/components/Layout/FlexBox";
import CountryFlag from "view/components/CountryFlag";
import RichText from "view/components/RichText";
import RichTextEditor from "view/components/RichTextEditor";
import type {RichTextEditorProps} from "view/components/RichTextEditor/types";
import type {ShowLanguage} from "view/components/SettingsRows/ShowLanguagesRow/useShowLanguages";
import AiTranslateButton from "./AiTranslateButton";
import {READ_ONLY_TRANSLATION_TOOLTIP} from "./LocalisationTextRow";
import useStyles from "./styles";

export type LocalisationRichTextOptions = Pick<
  RichTextEditorProps,
  | "controls"
  | "baseControls"
  | "matchers"
  | "imagePath"
  | "imageAdapter"
  | "externalRedirectShowId"
  | "showCustomFields"
  | "bindCustomFieldIds"
  | "customFieldShowNames"
  | "standCustomFields"
  | "variableSets"
  | "additionalVariables"
  | "maxHeight"
  | "maxLen"
>;

type Props = {
  editorId: string;
  language: ShowLanguage;
  value: string;
  revision: number;
  onChange: (value: string) => void;
  onTranslate: () => void;
  canTranslate: boolean;
  isTranslating: boolean;
  options?: LocalisationRichTextOptions;
  /** See LocalisationTextRow.readOnly — here the editor gives way to the published renderer. */
  readOnly?: boolean;
};

const LocalisationRichTextRow = ({
  editorId,
  language,
  value,
  revision,
  onChange,
  onTranslate,
  canTranslate,
  isTranslating,
  options,
  readOnly,
}: Props) => {
  const classes = useStyles();

  return (
    <FlexBox column gap={2} fullWidth className={classes.richTextSurface}>
      <FlexBox alignCenter justifyBetween className={classes.richTextHeader}>
        <FlexBox alignCenter gap={1}>
          <CountryFlag flagEmoji={language.flagEmoji} />
          <Typography variant="body1">{language.name}</Typography>
        </FlexBox>
        {readOnly ? (
          <Tooltip title={READ_ONLY_TRANSLATION_TOOLTIP}>
            <LockIcon
              fontSize="small"
              color="disabled"
              data-testid="localisation-lock"
            />
          </Tooltip>
        ) : (
          <AiTranslateButton
            onClick={onTranslate}
            disabled={!canTranslate}
            isLoading={isTranslating}
            tooltip={
              canTranslate
                ? `Generate the ${language.name} translation with AI`
                : "Add the English (UK) text first"
            }
          />
        )}
      </FlexBox>
      {readOnly ? (
        <div
          className={classes.richTextReadOnly}
          aria-label={`${language.name} translation`}
          aria-readonly
        >
          <RichText content={value} />
        </div>
      ) : (
        <RichTextEditor
          {...options}
          key={`${editorId}-${revision}`}
          id={editorId}
          toolbarId={`${editorId}-toolbar`}
          label={`${language.name} translation...`}
          defaultValue={value}
          onChange={onChange}
        />
      )}
    </FlexBox>
  );
};

export default LocalisationRichTextRow;
